import React, { useEffect, useState, FocusEvent, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandler, PaginationItem, Sort, SortDirection } from "@preveil-api";
import {
  Account, DriveUIActionTypes, GenericSortFieldType, SORT_DIRECTION, DriveLimits, CheckboxStatesTypes, sortBy, CheckboxStates, GenericListEntity, GenericEntryEntity,
  getOppositeSortDirection, PathInfo, PanelStateType, PanelState, NodePathInfoMap
} from "src/common";
import { Loading, Icon, Checkbox } from "src/components";
import { EmptyMessage, SidePanel } from "..";
import { ListRow } from ".";
import _ from "lodash";

type AllProps = {
    current_account: Account;
    is_loading: boolean;
    watched_list: GenericListEntity;
    selected: string[];
    filter?: string;
    pagination: PaginationItem;
    handleAction: ActionHandlerFunction;
}

function ListViewComponent(props: AllProps) {
    const { is_loading, current_account, watched_list, filter, selected, pagination, handleAction } = props;
    const [entries, setEntries] = useState<GenericEntryEntity[]>();
    const [detail_entry, setDetailEntry] = useState<GenericEntryEntity | undefined>(); // NOTE: Entry Loaded in DetailView
    const [panel_state, setPanelState] = useState<PanelStateType>(PanelState.off);
    const [sort, setSort] = useState<Sort<keyof GenericEntryEntity>>({ field: GenericSortFieldType.last_modification_date, direction: SORT_DIRECTION.descending });
    // Description: Maintainer nodes fetched grants
    const path_list_ref = useRef<NodePathInfoMap>(new Map<string, PathInfo[]>);
    function setPathListRef(id: string, _path_list_ref: PathInfo[]) {
        path_list_ref.current.set(id, _path_list_ref);
    }

    useEffect(() => {
        // NOTE: Set sorted entries after fetching watched_list && close side panel if opened on navigation
        handleSort(sort.field, sort.direction);
        (!!watched_list && panel_state === PanelState.on) &&
            ListViewRequests.handleToggleSidePanel(PanelState.off);
    }, [watched_list]);

    useEffect(() => {
        handleFilter(filter);
    }, [filter]);

    // Description: Filter entries by name or reset if filter is removed
    function handleFilter(search_term?: string) {
        if (!!search_term) {
            const _search_term = search_term.toLowerCase();
            const filtered = _.filter(watched_list.entries, (item: GenericEntryEntity) => item.name.toLowerCase().includes(_search_term));
            setEntries(filtered);
        } else {
            handleSort(sort.field, sort.direction);
        }
    }

    // Description: Sets the sort field with the new field. (if its the same field, it flips the direction)
    function handleSort(field: keyof GenericEntryEntity, _direction?: SortDirection) {
        const direction = !!_direction ? _direction :
            (sort.field === field ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending);
        setSort({ field, direction });
        const sorted_entries = sortBy(watched_list?.entries, { field, direction }, field === GenericSortFieldType.last_modification_date);
        setEntries(sorted_entries);
    }

    // Description: Get the selected all checkbox state
    function getSelectedState(): CheckboxStatesTypes {
        let ck_state: CheckboxStatesTypes = CheckboxStates.empty;
        if (!!pagination) {
            ck_state = (!selected.length ? CheckboxStates.empty :
                (selected.length < DriveLimits.DRIVE_PAGINATION_PAGE_SIZE && selected.length < pagination.totalRows) ?
                    CheckboxStates.indeterminate : CheckboxStates.checked);
        }
        return ck_state;
    }

    const ListViewRequests = {
        // Description: Set Location info
        handleSetPathInfo(params: { id: string, path_info: PathInfo[] }) {
            setPathListRef(params.id, params.path_info);
        },

        //  Description: Handle view details from menu
        handleDetails(entry: GenericEntryEntity) {
            setDetailEntry(entry);
            setPanelState(PanelState.on);
        },

        // Description: Toggle side panel used for close and reset, but can be used for open
        handleToggleSidePanel(_panel_state: PanelStateType) {
            setPanelState(_panel_state);
            if (_panel_state === PanelState.off) { // Reset states
                setTimeout(() => {
                    setDetailEntry(undefined);
                }, 500);
            }
        }
    };

    //  Description: Handle all actions from Children forms
    function handlePageActions(action_obj: IActionHandler) {
        const callback = `handle${action_obj.actionType}`;
        if ((ListViewRequests as any)[callback] instanceof Function) {
            (ListViewRequests as any)[callback](action_obj.params);
        } else {
            // NOTE: If not found here propagate to parent
            handleAction(action_obj);
        }
    }

    // Render methods
    // ------------------------------------------------
    // Descriptions: Shareable react fragment - for each field - returns the header with sort icon
    function headerWithSortIcon(name: string, field: string) {
        return (
            <>
                {name}
                {!!sort && sort.field === field && !!entries && entries.length > 1 && (
                    <Icon className={`${sort.direction === SORT_DIRECTION.descending ? "ficon-arrow-down" : "ficon-arrow-up"} small-icon ms-1`} />
                )}
            </>
        );
    }

    // Description: Render Header component
    function RenderHeader() {
        return <Row className="header-row">
            <Col md="1">
                <Checkbox className="sr-only"
                    onChange={(e: FocusEvent<HTMLInputElement>) => {
                        handleAction({ actionType: DriveUIActionTypes.Select, params: { selected: e.target.checked } });
                    }}
                    value="all" label="Select All" selected={getSelectedState()} />
            </Col>
            <Col md="5" className="col" onClick={() => handleSort(GenericSortFieldType.name)}>
                {headerWithSortIcon("Name", GenericSortFieldType.name)}
            </Col>
            <Col md="2" onClick={() => handleSort(GenericSortFieldType.type_label)}>
                {headerWithSortIcon("Type", GenericSortFieldType.type_label)}
            </Col>
            <Col md="3" onClick={() => handleSort(GenericSortFieldType.last_modification_date)}>
                {headerWithSortIcon("Modified", GenericSortFieldType.last_modification_date)}
            </Col>
            <Col md="1">
                <span hidden>actions</span>
            </Col>
        </Row>;
    }

    // Description: Render the rows
    function RenderList() {
        return <div className="list-body">
            {
                _.map(entries, (entry: GenericEntryEntity, i: number) => {
                    return <ListRow
                        key={`item_${i}`}
                        entry={entry}
                        path_info={path_list_ref.current.get(entry.id)}
                        selected={selected.includes(entry.id)}
                        handleAction={handlePageActions} />;
                })
            }
        </div>;
    }
    return <div className={`drive-list watched${is_loading ? " isloading" : ""}`}>
        {
            watched_list.entries.length > 0 ?
                <>
                    {RenderHeader()}
                    {RenderList()}
                </> :
                <EmptyMessage />
        }

        {
            is_loading && <Loading />
        }
        {
            !!detail_entry &&
            <SidePanel
                current_account={current_account}
                panel_state={panel_state}
                entry={detail_entry}
                path_info={path_list_ref.current.get(detail_entry.id)}
                handleAction={handlePageActions} />
        }
    </div>;
}

export default React.memo(ListViewComponent);